import type { ApiError, Resource } from "@sol-office/contracts";

export class RequestError extends Error {
  constructor(
    message: string,
    public code = "REQUEST_FAILED",
    public status = 0,
  ) {
    super(message);
  }
}

export async function api<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api/v1${path}`, {
      ...options,
      credentials: "include",
      headers: {
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        ...options.headers,
      },
    });
  } catch {
    throw new RequestError(
      "We couldn’t reach your workspace. Check your connection and try again.",
    );
  }
  if (response.status === 204) return undefined as T;
  const data = (await response.json().catch(() => null)) as T | ApiError | null;
  if (!response.ok) {
    const error =
      data && typeof data === "object" && "error" in data
        ? (data as ApiError).error
        : null;
    if (response.status === 401 && error?.code === "UNAUTHENTICATED")
      window.dispatchEvent(new Event("sol:session-expired"));
    throw new RequestError(
      error?.message ?? "Something went wrong. Please try again.",
      error?.code,
      response.status,
    );
  }
  return data as T;
}

export const post = <T>(path: string, body: unknown) =>
  api<T>(path, { method: "POST", body: JSON.stringify(body) });
export const patch = <T>(path: string, body: unknown) =>
  api<T>(path, { method: "PATCH", body: JSON.stringify(body) });

export function uploadFile(
  workspaceId: string,
  file: File,
  parentId: string | null,
  progress: (value: number) => void,
  signal: AbortSignal,
): Promise<Resource> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new RequestError("Upload cancelled.", "CANCELLED"));
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/v1/workspaces/${workspaceId}/uploads`);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Idempotency-Key", crypto.randomUUID());
    const abort = () => xhr.abort();
    signal.addEventListener("abort", abort, { once: true });
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable)
        progress(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onloadend = () => signal.removeEventListener("abort", abort);
    xhr.onerror = () =>
      reject(
        new RequestError(
          "Upload interrupted. Check your connection and try again.",
        ),
      );
    xhr.onabort = () =>
      reject(new RequestError("Upload cancelled.", "CANCELLED"));
    xhr.onload = () => {
      let data: {
        resource?: Resource;
        error?: { message: string; code: string };
      };
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        reject(new RequestError("The upload could not be completed."));
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300 && data.resource)
        resolve(data.resource);
      else
        reject(
          new RequestError(
            data.error?.message ?? "The upload could not be completed.",
            data.error?.code,
            xhr.status,
          ),
        );
    };
    const form = new FormData();
    form.append("file", file);
    if (parentId) form.append("parentId", parentId);
    xhr.send(form);
  });
}

export async function downloadFile(resource: Resource): Promise<void> {
  const response = await fetch(`/api/v1/resources/${resource.id}/download`, {
    credentials: "include",
  });
  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as ApiError | null;
    throw new RequestError(
      data?.error.message ?? "This file could not be downloaded.",
    );
  }
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = resource.name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";
}
